import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { usePreviewImport, useRunImport } from './queries';

type ImportKind = 'customers' | 'contacts';

/**
 * Kunden- und Kontaktimport -- F2-09, K-135.
 *
 * **Die Uebernahme steht erst nach der Vorschau zur Verfuegung.** Das ist die
 * ganze Absicht dieser Seite: Eine Kundenliste aus einer fremden Anwendung
 * enthaelt fast immer Doubletten, und ein Import ohne Vorschau erzeugt sie im
 * neuen System noch einmal -- nur diesmal ohne die Kenntnis, welche Zeile
 * welche war.
 *
 * Zeilen mit Fehlern werden gezeigt und uebersprungen, nicht abgelehnt: Eine
 * Datei mit einer fehlerhaften Zeile ist kein Grund, die anderen
 * neunhundertneunundneunzig nicht zu uebernehmen.
 */
export function ImportPage() {
  const { t } = useTranslation();
  const [kind, setKind] = useState<ImportKind>('customers');
  const [file, setFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const preview = usePreviewImport(kind);
  const run = useRunImport(kind);

  const reset = () => {
    setFile(null);
    preview.reset();
    run.reset();
    if (fileInput.current) fileInput.current.value = '';
  };

  return (
    <>
      <PageHeader
        breadcrumb={[t('crm.title'), t('crm.importTitle')]}
        title={t('crm.importTitle')}
        subtitle={t('crm.importSubtitle')}
      />

      <section className="card">
        <div className="row" role="group" aria-label={t('crm.importKind')}>
          <Button
            variant={kind === 'customers' ? 'secondary' : 'ghost'}
            aria-pressed={kind === 'customers'}
            onClick={() => {
              setKind('customers');
              reset();
            }}
          >
            {t('crm.title')}
          </Button>
          <Button
            variant={kind === 'contacts' ? 'secondary' : 'ghost'}
            aria-pressed={kind === 'contacts'}
            onClick={() => {
              setKind('contacts');
              reset();
            }}
          >
            {t('crm.contacts')}
          </Button>
        </div>

        <p className="field__hint">
          {kind === 'customers' ? t('crm.customerColumns') : t('crm.contactColumns')}
        </p>

        <div className="row">
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            className="field__control"
            aria-label={t('crm.chooseFile')}
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              run.reset();
              preview.reset();
            }}
          />
          <Button
            variant="secondary"
            disabled={!file}
            busy={preview.isPending}
            onClick={() => {
              if (file) preview.mutate(file);
            }}
          >
            {t('crm.previewAction')}
          </Button>
          <Button
            variant="primary"
            // Erst nach der Vorschau. Der Knopf ist gesperrt, solange sie
            // nicht vorliegt -- K-135 verlangt die Vorschau vor der Uebernahme.
            disabled={!file || !preview.data}
            busy={run.isPending}
            onClick={() => {
              if (file) run.mutate(file);
            }}
          >
            {t('crm.importAction')}
          </Button>
        </div>

        {preview.isError ? <ErrorNotice error={preview.error} /> : null}
        {run.isError ? <ErrorNotice error={run.error} /> : null}
      </section>

      {run.data ? (
        <div className="notice notice--warning" role="status">
          {t('crm.importDone', {
            created: run.data.created,
            updated: run.data.updated,
            skipped: run.data.skipped,
          })}
        </div>
      ) : null}

      {preview.data ? (
        <>
          <div className="stat-row">
            <div className="stat">
              <span className="stat__value">{preview.data.would_create}</span>
              <span className="stat__label">{t('crm.wouldCreate')}</span>
            </div>
            <div className="stat">
              <span className="stat__value">{preview.data.would_update}</span>
              <span className="stat__label">{t('crm.wouldUpdate')}</span>
            </div>
            <div className="stat">
              <span className="stat__value">{preview.data.would_skip}</span>
              <span className="stat__label">{t('crm.wouldSkip')}</span>
            </div>
          </div>

          <div className="card card--flush">
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('crm.line')}</th>
                    <th scope="col">{t('crm.action')}</th>
                    <th scope="col">{t('crm.values')}</th>
                    <th scope="col">{t('crm.problems')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.data.rows.map((row) => (
                    <tr key={row.line}>
                      <td className="table__number">{row.line}</td>
                      <td>
                        <Badge tone={toneForAction(row.action)}>
                          {t(`importAction.${row.action}`, { defaultValue: row.action })}
                        </Badge>
                      </td>
                      <td className="table__muted">
                        {Object.entries(row.values)
                          .filter(([, value]) => value)
                          .map(([key, value]) => `${key}: ${value}`)
                          .join(' · ')}
                      </td>
                      <td>
                        {(row.errors ?? []).length > 0 ? (
                          <span className="field__error">{(row.errors ?? []).join(' ')}</span>
                        ) : (
                          <span className="table__muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

function toneForAction(action: string) {
  if (action === 'create') return 'success' as const;
  if (action === 'update') return 'info' as const;
  return 'danger' as const;
}
